import { useEffect, useCallback, forwardRef, useState } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Dialog, Slide, Typography, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { removeFileState } from "./util/editor";
import {
  getCurrentStatus,
  getIsWorkflowSaving,
  getIsWorkflowSaved,
  updateWorkflow,
  getWorkflowData,
} from "store/editor";
import WorkflowStatus from "./components/workflowStatus";
import ArrowForward from "@mui/icons-material/ArrowForward";
import VerificationDialog from "./components/VerificationDialog";
import {
  getNextStatusFromWorkflow,
  requiresVerification,
  getVerificationType,
} from "./config/projectPipelines";
import { isUserAdmin } from "utils/userHelpers";
import { findProfanityMatches } from "utils/profanityFilter";
import { useDarkMode } from "./useDarkMode";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function SaveWorkflowModal({ open, setOpen }) {
  const isDarkMode = useDarkMode();

  const styles = {
    root: {
      pb: "48px",
      px: "80px",
    },

    topRow: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      mb: "20px",
    },

    textContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      mb: "48px",
    },

    buttonContainer: {
      display: "flex",
      gap: "12px",
    },

    heading: {
      fontSize: "24px",
      fontWeight: 700,
      lineHeight: "35px",
      color: isDarkMode ? "#FFF" : "#0F0F0F",
      mb: "12px",
    },

    text: {
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "22px",
      color: isDarkMode ? "#878D98" : "#667085",
    },

    closeIcon: {
      position: "absolute",
      right: 24,
      top: 16,
      color: "#667085",
    },

    arrowIcon: {
      color: "#B2B6BD",
      height: "18px",
      width: "24px",
      mx: "24px",
    },

    progress: {
      color: "#fff",
    },
  };

  const workflowStatus = useSelector(getCurrentStatus);
  const isSaving = useSelector(getIsWorkflowSaving);
  const isSaved = useSelector(getIsWorkflowSaved);
  const workflowData = useSelector(getWorkflowData);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationType, setVerificationType] = useState(null);
  const [showProfanityDialog, setShowProfanityDialog] = useState(false);
  const [canOverrideProfanity, setCanOverrideProfanity] = useState(false);
  const [profanityOverrideApproved, setProfanityOverrideApproved] = useState(false);
  const [profanityMatches, setProfanityMatches] = useState([]);
  const dispatch = useDispatch();
  const store = useStore();
  const history = useNavigate();

  const proceedWithChecks = (profanityOverride = false) => {
    setProfanityOverrideApproved(profanityOverride);
    onComplete(profanityOverride);
  };

  // Runs profanity checks and proceeds with submission flow
  const continueWithValidation = () => {
    const latestTranscripts = store.getState()?.editor?.transcripts || [];
    const matches = findProfanityMatches(latestTranscripts);
    const isAdmin = isUserAdmin();

    if (matches.length > 0) {
      handleClose();
      setProfanityMatches(matches);
      setCanOverrideProfanity(isAdmin);
      setShowProfanityDialog(true);
      return;
    }

    proceedWithChecks(false);
  };

  const onComplete = async (profanityOverride = false) => {
    // On second click (or if no errors), proceed regardless of validation status
    // Check if verification is required for this status
    if (requiresVerification(workflowData, workflowStatus)) {
      const type = getVerificationType(workflowData, workflowStatus);
      setVerificationType(type);

      // Show the verification dialog
      setShowVerificationDialog(true);
      // Hide the regular completion dialog
      handleClose();
      return;
    }
    dispatch(updateWorkflow({ allowProfanityOverride: profanityOverride }));
    removeFileState(workflowData?.id);
  };

  const onVerificationComplete = () => {
    // All checkboxes are checked, proceed with workflow update
    setShowVerificationDialog(false);
    dispatch(updateWorkflow({ allowProfanityOverride: profanityOverrideApproved }));
    removeFileState(workflowData?.id);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleVerificationClose = () => {
    setShowVerificationDialog(false);
  };

  const handleProfanityDialogClose = () => {
    setShowProfanityDialog(false);
    setCanOverrideProfanity(false);
    setProfanityOverrideApproved(false);
    setProfanityMatches([]);
  };

  const handleProfanityDialogContinue = () => {
    setShowProfanityDialog(false);
    proceedWithChecks(true);
  };

  useEffect(() => {
    if (isSaved) {
      handleClose();
      setShowVerificationDialog(false);
    }
  }, [isSaved, handleClose, dispatch, history]);

  return (
    <div>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "24px",
            width: "100%",
            maxWidth: "560px",
            backgroundColor: isDarkMode ? "#26282C" : "#f6f6f6",
          },
        }}
      >
        <Box
          sx={{ display: "flex", justifyContent: "end", padding: "16px 24px" }}
        >
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon sx={{ color: isDarkMode ? "#667085" : "#101828" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...styles.root,
          }}
        >
          <Box sx={styles.topRow}>
            <WorkflowStatus
              label={workflowStatus}
              enabled
              show
              isDarkMode={isDarkMode}
            />
            <ArrowForward sx={styles.arrowIcon} />
            <WorkflowStatus
              label={getNextStatusFromWorkflow(workflowData, workflowStatus)}
              enabled
              isDarkMode={isDarkMode}
              show
            />
          </Box>

          <Box sx={styles.textContainer}>
            <Typography sx={styles.heading}>Complete Current Stage</Typography>
            <Typography sx={styles.text}>
              Are you sure you want to submit this file? This decision cannot be
              undone.
            </Typography>
          </Box>

          <Box sx={styles.buttonContainer}>
            <Button
              variant="outlined-secondary"
              sx={{
                flex: 1,
                color: isDarkMode ? "#FFF" : "#333333",
                fontWeight: 700,
                border: `thin solid ${isDarkMode ? "#71717A" : "#D9d9D9"}`,

                "&:hover": {
                  color: isDarkMode ? "#FFF" : "#333333",
                  backgroundColor: isDarkMode ? "#31353C" : "#F2F4F7",
                },
              }}
              onClick={handleClose}
            >
              Cancel
            </Button>

            <LoadingButton
              variant="contained"
              sx={{ flex: 1 }}
              onClick={continueWithValidation}
              loading={isSaving}
            >
              Yes, Proceed
            </LoadingButton>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={showProfanityDialog}
        TransitionComponent={Transition}
        onClose={handleProfanityDialogClose}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "20px",
            width: "100%",
            maxWidth: "520px",
            backgroundColor: isDarkMode ? "#26282C" : "#f6f6f6",
          },
        }}
      >
        <Box sx={{ p: "32px" }}>
          <Typography sx={styles.heading}>Submission Blocked</Typography>
          <Typography sx={styles.text}>
            {canOverrideProfanity
              ? "Profanity was detected in this transcript. As an admin, you can continue or cancel and edit first."
              : "Profanity was detected in this transcript. Please remove profanity before submitting this stage."}
          </Typography>
          {profanityMatches.length > 0 && (
            <Box sx={{
              mt: "16px",
              maxHeight: "200px",
              overflowY: "auto",
              borderRadius: "8px",
              backgroundColor: isDarkMode ? "#1E2025" : "#FFF",
              border: `1px solid ${isDarkMode ? "#3A3D44" : "#E4E7EC"}`,
            }}>
              {profanityMatches.map((match, index) => (
                <Box
                  key={index}
                  sx={{
                    p: "10px 14px",
                    borderBottom: index < profanityMatches.length - 1
                      ? `1px solid ${isDarkMode ? "#3A3D44" : "#E4E7EC"}`
                      : "none",
                  }}
                >
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: isDarkMode ? "#878D98" : "#667085", mb: "2px" }}>
                    {match.timestamp}{match.speaker ? ` — ${match.speaker}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: isDarkMode ? "#D0D5DD" : "#344054" }}>
                    {match.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ mt: "24px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            {canOverrideProfanity ? (
              <>
                <Button variant="outlined-secondary" onClick={handleProfanityDialogClose}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleProfanityDialogContinue}>
                  Continue
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={handleProfanityDialogClose}>
                OK
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Verification Checklist Dialog */}
      <VerificationDialog
        open={showVerificationDialog}
        onClose={handleVerificationClose}
        onSubmit={onVerificationComplete}
        verificationType={verificationType}
        isSaving={isSaving}
      />

    </div>
  );
}
